import { SmallAddIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Center,
  chakra,
  Flex,
  HStack,
  Link,
  Text,
  useBoolean,
  usePrevious,
  VStack,
  Wrap,
} from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { MathJax } from "better-react-mathjax";
import { calculationFormSchema } from "features/calculation/CalculationFormSchema";
import { useMetricList } from "gen/endpoints/metrics/metrics";
import {
  getTransactionCalculationAndFeeBasisListQueryKey,
  useTransactionCalculationListSuspense,
  useTransactionCodeConfigListSuspense,
} from "gen/endpoints/transactions/transactions";
import { AdvancedCalculationLoanPropertyTypeEnum } from "gen/model/advancedCalculationLoanPropertyTypeEnum";
import { CalculationClassificationEnum } from "gen/model/calculationClassificationEnum";
import { CalculationDefinitionCreatePayloadData } from "gen/model/calculationDefinitionCreatePayloadData";
import { CalculationTypeEnum } from "gen/model/calculationTypeEnum";
import { ReferenceRateTypeEnum } from "gen/model/referenceRateTypeEnum";
import { TermReferenceRatesEnum } from "gen/model/termReferenceRatesEnum";
import { TransactionCalculationPayloadCreateDataRequest } from "gen/model/transactionCalculationPayloadCreateDataRequest";
import { parse as mathParse } from "mathjs";
import React, { useContext, useEffect, useMemo, useRef } from "react";
import {
  DefaultValues,
  useFieldArray,
  useForm,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { QueryBoundary } from "shared/api/QueryBoundary";
import { FormCancelButton } from "shared/components/button/FormCancelButton";
import { FormSubmitButton } from "shared/components/button/FormSubmitButton";
import {
  FormToggleButtonGroup,
  ToggleButton,
} from "shared/components/button/ToggleButton";
import Collapsible from "shared/components/collapsible/Collapsible";
import Form from "shared/components/form/Form";
import {
  FormFieldPrefix,
  useFormFieldPrefix,
  usePrefixedFieldName,
} from "shared/components/form/FormFieldPrefix";
import { FormFieldset } from "shared/components/form/FormFieldset";
import {
  FormFooter,
  FormFooterActions,
} from "shared/components/form/FormFooter";
import { FormHiddenInput } from "shared/components/form/FormHiddenInput";
import { FormUnsavedIndicator } from "shared/components/form/FormUnsavedIndicator";
import { FormCheckbox } from "shared/components/input/checkbox/Checkbox";
import { FormDateInput } from "shared/components/input/date/DateInput";
import { InvestmentTypeFormSelect } from "shared/components/input/select/InvestmentTypeSelect";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { FormSimpleSelect } from "shared/components/input/select/SimpleSelect";
import { FormSimpleTextInput } from "shared/components/input/text/SimpleTextInput";
import { FormTextArea } from "shared/components/input/text/TextArea";
import { FormTextInput } from "shared/components/input/text/TextInput";
import { TableRemoveButton } from "shared/components/table/TableRowActionButtons";
import { rootRoute } from "shared/navigation/Routes";
import { isObjectEmpty } from "shared/utils/ObjectUtils";
import { getChildAsString } from "shared/utils/ReactUtils";
import { match } from "ts-pattern";

export interface CalculationFormProps {
  fundFamilyId: string;
  transactionCalculationId?: string;
  onSubmit: (data: TransactionCalculationPayloadCreateDataRequest) => void;
  defaultValues: DefaultValues<TransactionCalculationPayloadCreateDataRequest>;
}

enum CalculationEquationType {
  basic = "basic",
  basic_fee_basis = "basic_fee_basis",
  advanced = "advanced",
}

interface CalculationEquationProps {
  type: CalculationEquationType;
  index: number;
  fundFamilyId: string;
  transactionCalculationId?: string;
  onClickRemove: (index: number) => void;
  classification: CalculationClassificationEnum;
}

export const makeEmptyBasicCalculationDefinition =
  (): CalculationDefinitionCreatePayloadData => ({
    start_date: null,
    end_date: null,
    eval_string: "",
    transaction_codes: [
      {
        transaction_code_id: "",
        add_or_subtract: null as never,
      },
    ],
    calculation_components: [],
    transaction_filters: {},
    fee_basis_categories: [],
  });

const makeEmptyAdvancedCalculationDefinition =
  (): CalculationDefinitionCreatePayloadData => ({
    start_date: null,
    end_date: null,
    eval_string: "",
    transaction_codes: [],
    calculation_components: [],
    transaction_filters: {},
    fee_basis_categories: [],
  });

const makeUploadedFeeBasisCalculationDefinition =
  (): CalculationDefinitionCreatePayloadData => ({
    start_date: null,
    end_date: null,
    eval_string: "",
    transaction_codes: [],
    calculation_components: [],
    transaction_filters: {},
    fee_basis_categories: [
      {
        category_name: "",
      },
    ],
  });

const makeEmptyCalculationDefinition = (type: CalculationEquationType) =>
  match(type)
    .with(CalculationEquationType.basic, () =>
      makeEmptyBasicCalculationDefinition(),
    )
    .with(CalculationEquationType.basic_fee_basis, () =>
      makeUploadedFeeBasisCalculationDefinition(),
    )
    .with(CalculationEquationType.advanced, () =>
      makeEmptyAdvancedCalculationDefinition(),
    )
    .exhaustive();

const getFieldArrayPrefix = (index: number) =>
  `calculation_definitions.${index}`;

export const CalculationForm: React.FC<CalculationFormProps> = ({
  fundFamilyId,
  transactionCalculationId,
  onSubmit,
  defaultValues,
}) => {
  const { t } = useTranslation(["common", "calculation", "enums", "errors"]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const form = useForm<TransactionCalculationPayloadCreateDataRequest>({
    defaultValues,
    reValidateMode: "onChange",
    resolver: zodResolver(calculationFormSchema),
  });

  const {
    append: appendDefinition,
    remove: removeDefinition,
    fields: calculationDefinitions,
  } = useFieldArray({
    control: form.control,
    name: "calculation_definitions",
  });

  const classification = form.watch("classification") as
    | CalculationClassificationEnum
    | undefined;
  const type = form.watch("calculation_type");

  const isFeeBasis = classification === CalculationClassificationEnum.fee_basis;

  const equationType = match({ type, isFeeBasis })
    .with(
      { type: CalculationTypeEnum.basic, isFeeBasis: true },
      () => CalculationEquationType.basic_fee_basis,
    )
    .with(
      { type: CalculationTypeEnum.basic },
      () => CalculationEquationType.basic,
    )
    .with(
      { type: CalculationTypeEnum.advanced },
      () => CalculationEquationType.advanced,
    )
    .otherwise(() => undefined);

  const prevEquationType = usePrevious(equationType);

  useEffect(() => {
    if (!transactionCalculationId && prevEquationType !== equationType) {
      form.setValue(
        "calculation_definitions",
        equationType ? [makeEmptyCalculationDefinition(equationType)] : [],
      );
    }
  }, [equationType, form, prevEquationType, transactionCalculationId, type]);

  const onClickAdd = () => {
    if (equationType) {
      appendDefinition(makeEmptyCalculationDefinition(equationType));
    }
  };

  const navigateToCalculations = () => {
    navigate(
      rootRoute({})
        .fundFamilies({})
        .fundFamily({ fundFamilyId })
        .setup({})
        .fund({})
        .calculations({}).$,
    );
  };

  return (
    <Form
      form={form}
      onSubmit={onSubmit}
      onSuccess={async () => {
        await queryClient.invalidateQueries({
          queryKey:
            getTransactionCalculationAndFeeBasisListQueryKey(fundFamilyId),
        });
        navigateToCalculations();
      }}
    >
      <VStack alignItems="stretch" gap="20px">
        <Card variant="form">
          <CardHeader>
            <Text textStyle="bodyBold">{t("calculation:basicDetails")}</Text>
          </CardHeader>
          <CardBody>
            <FormTextInput
              name="name"
              title={t("calculation:calculationName")}
            />
            <FormSelect
              title={t("calculation:classification")}
              name="classification"
              isDisabled={!!transactionCalculationId}
              shouldUnregister={false}
            >
              {Object.values(CalculationClassificationEnum).map((key) => (
                <SelectOption value={key} key={key}>
                  {t(`enums:CalculationClassificationEnum.${key}`)}
                </SelectOption>
              ))}
            </FormSelect>
            <CalculationTypeSelect
              classification={classification}
              isDisabled={!!transactionCalculationId}
            />
          </CardBody>
        </Card>
        {equationType && classification && (
          <Card variant="form">
            <CardHeader>
              <Text textStyle="bodyBold">
                {isFeeBasis
                  ? t("calculation:createFeeBasis")
                  : t("calculation:createEquation")}
              </Text>
            </CardHeader>
            <CardBody
              {...(type === CalculationTypeEnum.advanced
                ? {
                    maxWidth: "1182px",
                  }
                : undefined)}
            >
              <QueryBoundary>
                {!isFeeBasis && (
                  <Text textStyle="small" mb="8px">
                    {type === CalculationTypeEnum.basic
                      ? t("calculation:equationInstructionsBasic")
                      : t("calculation:equationInstructionsAdvanced")}
                  </Text>
                )}
                <VStack alignItems="stretch" spacing="16px">
                  {calculationDefinitions.map((definition, index) => (
                    <CalculationEquation
                      key={definition.id}
                      index={index}
                      type={equationType}
                      fundFamilyId={fundFamilyId}
                      transactionCalculationId={transactionCalculationId}
                      onClickRemove={removeDefinition}
                      classification={classification}
                    />
                  ))}
                  {!isFeeBasis && (
                    <Button
                      size="sm"
                      colorScheme="secondary"
                      iconSpacing="2px"
                      leftIcon={<SmallAddIcon />}
                      onClick={onClickAdd}
                    >
                      {t("calculation:addTimePeriod")}
                    </Button>
                  )}
                </VStack>
              </QueryBoundary>
            </CardBody>
          </Card>
        )}
      </VStack>
      <FormFooter>
        <FormUnsavedIndicator />
        <FormFooterActions>
          <FormCancelButton onClick={navigateToCalculations} />
          <FormSubmitButton>{t("common:save")}</FormSubmitButton>
        </FormFooterActions>
      </FormFooter>
    </Form>
  );
};

const CalculationEquation: React.FC<CalculationEquationProps> = (props) => {
  switch (props.type) {
    case CalculationEquationType.basic:
      return <CalculationEquationBasic {...props} />;
    case CalculationEquationType.basic_fee_basis:
      return <CalculationEquationBasicFeeBasis {...props} />;
    case CalculationEquationType.advanced:
      return <CalculationEquationAdvanced {...props} />;
  }
};

const CalculationEquationBasic: React.FC<CalculationEquationProps> = ({
  index,
  fundFamilyId,
  onClickRemove,
  classification,
}) => {
  const { t } = useTranslation(["calculation", "common"]);
  const form = useFormContext<TransactionCalculationPayloadCreateDataRequest>();
  const fieldNamePrefix = getFieldArrayPrefix(index);

  const {
    fields: transactionCodes,
    append: appendTransactionCode,
    remove: removeTransactionCode,
  } = useFieldArray({
    control: form.control,
    name: `${fieldNamePrefix}.transaction_codes` as "calculation_definitions.0.transaction_codes",
  });

  return (
    <FormFieldPrefix prefix={fieldNamePrefix}>
      <Box
        p="16px"
        bg="gray.25"
        borderWidth="1px"
        borderColor="gray.300"
        borderStyle="dashed"
        borderRadius="8px"
      >
        <Text textStyle="overline" pt="8px" pb="16px">
          {t("calculation:rangeSection")}
        </Text>
        <HStack spacing="16px">
          <FormDateInput
            name="start_date"
            title={t("common:startDate")}
            shouldUnregister={false}
          />
          <FormDateInput
            name="end_date"
            title={t("common:endDate")}
            shouldUnregister={false}
          />
        </HStack>
        <Text textStyle="overline" pt="8px" pb="16px">
          {t("calculation:calculation")}
        </Text>
        <QueryBoundary>
          {transactionCodes.map((code, index) => (
            <CalculationDefinitionCode
              key={code.id}
              index={index}
              fundFamilyId={fundFamilyId}
              onClickRemove={removeTransactionCode}
              classification={classification}
            />
          ))}
        </QueryBoundary>
        <Link
          textStyle="small"
          onClick={() => appendTransactionCode({} as never)}
        >
          {t("calculation:addTransactionCode")}
        </Link>
        <CalculationFilters fundFamilyId={fundFamilyId} />
        <CalculationDefinitionRemoveButton
          onClick={() => onClickRemove(index)}
        />
      </Box>
    </FormFieldPrefix>
  );
};

const CalculationFilters: React.FC<{
  fundFamilyId: string;
}> = () => {
  const { t } = useTranslation("calculation");
  const fieldNamePrefix = "transaction_filters";
  // TODO ENG-8089: consistent spacing
  // TODO ENG-8081: update this to use the NamedDateSelect component
  const prefix = useFormFieldPrefix();
  const form = useFormContext();
  const section: TransactionCalculationPayloadCreateDataRequest["calculation_definitions"][number] =
    useWatch({
      name: prefix,
      control: form.control,
    });
  const filters = section.transaction_filters;
  // we only want this to run on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isFilterUnset = useMemo(() => isObjectEmpty(filters), []);

  const [isOpen, { toggle: toggleOpen }] = useBoolean(!isFilterUnset);

  return (
    <Collapsible
      isOpen={isOpen}
      toggleOpen={toggleOpen}
      label={t("calculation:calculationFilters.title")}
      mt="16px"
    >
      <FormFieldPrefix prefix={fieldNamePrefix}>
        <VStack spacing={0} mt="16px" alignItems="flex-start">
          <Flex width="100%" columnGap="16px">
            <Box flex={1}>
              <FormDateInput
                name="start_date"
                title={t(
                  "calculation:calculationFilters.filterTypes.startDate.label",
                )}
              />
            </Box>
            <Box flex={1}>
              <FormDateInput
                name="end_date"
                title={t(
                  "calculation:calculationFilters.filterTypes.endDate.label",
                )}
              />
            </Box>
          </Flex>

          <InvestmentTypeFormSelect
            name="investment_type"
            unsetWhenEmpty={true}
          />
          <FormCheckbox
            name="include_draft_transactions"
            title={t(
              "calculation:calculationFilters.filterTypes.includeDraftTransactions.label",
            )}
            shouldUnregister={false}
          >
            {t(
              "calculation:calculationFilters.filterTypes.includeDraftTransactions.label",
            )}
          </FormCheckbox>
          {/* TODO ENG-8081: include limitToEventPeriod  */}
        </VStack>
      </FormFieldPrefix>
    </Collapsible>
  );
};

const CalculationEquationBasicFeeBasis: React.FC<CalculationEquationProps> = ({
  index,
  fundFamilyId,
  transactionCalculationId,
}) => {
  const { t } = useTranslation(["calculation"]);
  const form = useFormContext<TransactionCalculationPayloadCreateDataRequest>();
  const fieldNamePrefix = getFieldArrayPrefix(index);

  const {
    fields: feeBasisCategories,
    append: appendFeeBasisCategory,
    remove: removeFeeBasisCategory,
  } = useFieldArray({
    control: form.control,
    name: `${fieldNamePrefix}.fee_basis_categories` as "calculation_definitions.0.transaction_codes",
  });

  return (
    <FormFieldPrefix prefix={fieldNamePrefix}>
      <Box
        p="16px"
        bg="gray.25"
        borderWidth="1px"
        borderColor="gray.300"
        borderStyle="dashed"
        borderRadius="8px"
      >
        <Text textStyle="overline" pt="8px" pb="16px">
          {t("calculation:uploadedCategories")}
        </Text>
        {feeBasisCategories.map((code, index) => (
          <CalculationDefinitionCategoryName
            key={code.id}
            index={index}
            fundFamilyId={fundFamilyId}
            onClickRemove={removeFeeBasisCategory}
            isReadOnly={!!transactionCalculationId}
          />
        ))}
        {!transactionCalculationId && (
          <Link
            textStyle="small"
            onClick={() => appendFeeBasisCategory({} as never)}
          >
            {t("calculation:addCategory")}
          </Link>
        )}
      </Box>
    </FormFieldPrefix>
  );
};

const CalculationFormContext = React.createContext<{
  formulaRef: React.RefObject<HTMLTextAreaElement>;
}>({
  formulaRef: { current: null },
});

const CalculationEquationAdvanced: React.FC<CalculationEquationProps> = ({
  index,
  fundFamilyId,
  transactionCalculationId,
  onClickRemove,
  classification,
}) => {
  const { t } = useTranslation(["calculation", "common", "enums"]);

  const isFeeBasis = classification === CalculationClassificationEnum.fee_basis;

  const { data: metricList } = useMetricList(fundFamilyId);

  const { data: calcsList } = useTransactionCalculationListSuspense(
    fundFamilyId,
    { get_only_fee_basis_categories: isFeeBasis },
    {
      query: {
        select: (data) =>
          data.filter(
            (c) =>
              (!transactionCalculationId ||
                c.id !== transactionCalculationId) &&
              c.classification !== CalculationClassificationEnum.interest_rate,
          ),
      },
    },
  );

  const form = useFormContext<TransactionCalculationPayloadCreateDataRequest>();

  const fieldNamePrefix = getFieldArrayPrefix(index);
  const evalStringFieldName =
    `${fieldNamePrefix}.eval_string` as "calculation_definitions.0.eval_string";

  const evalString = form.watch(evalStringFieldName);

  const evalStringTex = useMemo(() => {
    try {
      return evalString?.trim().length
        ? `$$${mathParse(evalString).toTex({})}$$`
        : "";
    } catch (e) {
      return "";
    }
  }, [evalString]);

  const formulaRef = useRef<HTMLTextAreaElement>(null);

  return (
    <CalculationFormContext.Provider value={{ formulaRef }}>
      <FormFieldPrefix prefix={fieldNamePrefix}>
        <Box
          w="100%"
          p="16px"
          bg="gray.25"
          borderWidth="1px"
          borderColor="gray.300"
          borderStyle="dashed"
          borderRadius="8px"
        >
          {!isFeeBasis &&
            classification !== CalculationClassificationEnum.interest_rate && (
              <HStack
                spacing="16px"
                justifyContent="flex-start"
                maxWidth="526px"
              >
                <FormDateInput
                  name="start_date"
                  title={t("common:startDate")}
                  shouldUnregister={false}
                />
                <FormDateInput
                  name="end_date"
                  title={t("common:endDate")}
                  shouldUnregister={false}
                />
              </HStack>
            )}
          <HStack alignItems="flex-start" spacing="18px">
            <Box width="334px">
              <FormFieldset variant="solid">
                <chakra.legend textStyle="small">
                  {t("calculation:operators")}
                </chakra.legend>
                <HStack spacing="8px">
                  <OperatorButton>+</OperatorButton>
                  <OperatorButton>-</OperatorButton>
                  {!isFeeBasis && (
                    <>
                      <OperatorButton>*</OperatorButton>
                      <OperatorButton>/</OperatorButton>
                      <OperatorButton>(</OperatorButton>
                      <OperatorButton>)</OperatorButton>
                      <OperatorButton>min</OperatorButton>
                      <OperatorButton>max</OperatorButton>
                    </>
                  )}
                </HStack>
              </FormFieldset>
              {!isFeeBasis && (
                <FormFieldset variant="solid">
                  <chakra.legend textStyle="small">
                    {t("calculation:metrics")}
                  </chakra.legend>
                  <Wrap spacing="8px">
                    {classification ===
                    CalculationClassificationEnum.interest_rate
                      ? Object.values(
                          AdvancedCalculationLoanPropertyTypeEnum,
                        ).map((loan_property) => (
                          <OperatorButton key={loan_property}>
                            {t(
                              `enums:AdvancedCalculationLoanPropertyTypeEnum.${
                                loan_property as AdvancedCalculationLoanPropertyTypeEnum
                              }`,
                            )}
                          </OperatorButton>
                        ))
                      : metricList?.map((metric) => (
                          <OperatorButton key={metric.metric_name}>
                            {metric.metric_name}
                          </OperatorButton>
                        ))}
                  </Wrap>
                </FormFieldset>
              )}
              {classification ===
                CalculationClassificationEnum.interest_rate && (
                <FormFieldset variant="solid">
                  <chakra.legend textStyle="small">
                    {t("calculation:referenceRates")}
                  </chakra.legend>
                  <Wrap spacing="8px">
                    {Object.values(ReferenceRateTypeEnum).map((rate) => (
                      <OperatorButton key={rate}>
                        {t(
                          `enums:ReferenceRateTypeEnum.${
                            rate as ReferenceRateTypeEnum
                          }`,
                        )}
                      </OperatorButton>
                    ))}
                    {Object.values(TermReferenceRatesEnum).map((rate) => (
                      <OperatorButton key={rate}>
                        {t(
                          `enums:TermReferenceRatesEnum.${
                            rate as TermReferenceRatesEnum
                          }`,
                        )}
                      </OperatorButton>
                    ))}
                  </Wrap>
                </FormFieldset>
              )}
              {classification !==
                CalculationClassificationEnum.interest_rate && (
                <FormFieldset variant="solid">
                  <chakra.legend textStyle="small">
                    {t("calculation:calculations")}
                  </chakra.legend>
                  <Wrap spacing="8px">
                    {calcsList.map((calc) => (
                      <OperatorButton key={calc.name}>
                        {calc.name}
                      </OperatorButton>
                    ))}
                  </Wrap>
                </FormFieldset>
              )}
            </Box>
            <Box flex="1" overflow="hidden">
              <FormHiddenInput
                name="calculation_components"
                defaultValue={calcsList.map((c) => c.id)}
                shouldUnregister={false}
              />
              <FormTextArea
                ref={formulaRef}
                title={t("calculation:formula")}
                name="eval_string"
                minHeight="222px"
                controlProps={{
                  mt: "8px",
                }}
                wrapperProps={{
                  borderColor: "gray.200",
                  borderRadius: "4px",
                }}
                shouldUnregister={false}
              />
              <FormFieldset variant="overline" minWidth="0">
                <chakra.legend textStyle="small">
                  {t("calculation:preview")}
                </chakra.legend>
                <Center
                  minHeight="138px"
                  fontSize="24px"
                  w="100%"
                  sx={{
                    svg: {
                      width: "100%",
                    },
                  }}
                >
                  <MathJax dynamic={true}>{evalStringTex}</MathJax>
                </Center>
              </FormFieldset>
            </Box>
          </HStack>
          {!isFeeBasis && (
            <CalculationDefinitionRemoveButton
              onClick={() => onClickRemove(index)}
            />
          )}
        </Box>
      </FormFieldPrefix>
    </CalculationFormContext.Provider>
  );
};

const CalculationDefinitionCode: React.FC<{
  fundFamilyId: string;
  index: number;
  onClickRemove: (index: number) => void;
  classification: CalculationClassificationEnum;
}> = ({ fundFamilyId, index, onClickRemove, classification }) => {
  const { t } = useTranslation(["calculation"]);

  const { data: transactionsConfigList } = useTransactionCodeConfigListSuspense(
    fundFamilyId,
    classification === CalculationClassificationEnum.net_asset_value
      ? {
          transaction_type: "hypothetical",
          call_or_distribution: "distribution",
        }
      : classification === CalculationClassificationEnum.fee_basis_category
        ? {} // Allow fee basis category to be based on any transaction code, including portfolio transactions
        : { transaction_type: "capital" },
  );

  return (
    <FormFieldPrefix prefix={`transaction_codes.${index}`}>
      <HStack pb="10px">
        <FormToggleButtonGroup
          name="add_or_subtract"
          size="sm"
          showMessages={false}
          controlProps={{
            mb: 0,
          }}
          shouldUnregister={false}
        >
          <ToggleButton value="add">+</ToggleButton>
          <ToggleButton value="subtract">-</ToggleButton>
        </FormToggleButtonGroup>
        <FormSimpleSelect
          name="transaction_code_id"
          size="sm"
          title={t("calculation:selectTransactionTag")}
          shouldUnregister={false}
        >
          {transactionsConfigList.map(({ transaction_code_id, rich_name }) => (
            <SelectOption value={transaction_code_id} key={transaction_code_id}>
              {rich_name!}
            </SelectOption>
          ))}
        </FormSimpleSelect>
        <TableRemoveButton onClick={() => onClickRemove(index)} />
      </HStack>
    </FormFieldPrefix>
  );
};

const CalculationDefinitionCategoryName: React.FC<{
  fundFamilyId: string;
  index: number;
  onClickRemove: (index: number) => void;
  isReadOnly: boolean;
}> = ({ index, onClickRemove, isReadOnly }) => {
  const { t } = useTranslation(["calculation"]);
  return (
    <FormFieldPrefix prefix={`fee_basis_categories.${index}`}>
      <HStack pb="10px">
        <FormSimpleTextInput
          name="category_name"
          placeholder={t("calculation:categoryName")}
          size="sm"
          isReadOnly={isReadOnly}
          shouldUnregister={false}
        />
        {!isReadOnly && (
          <TableRemoveButton onClick={() => onClickRemove(index)} />
        )}
      </HStack>
    </FormFieldPrefix>
  );
};

const OperatorButton: React.FC<React.PropsWithChildren> = ({ children }) => {
  const form = useFormContext();
  const fieldName = usePrefixedFieldName("eval_string");

  const { formulaRef } = useContext(CalculationFormContext);
  const childString = `${getChildAsString(children)} `;
  const operator =
    childString === "min "
      ? "min("
      : childString === "max "
        ? "max("
        : childString;
  return (
    <Badge
      variant="button"
      onMouseDownCapture={(e) => {
        e.preventDefault();
      }}
      onClick={() => {
        const textarea = formulaRef.current;
        if (textarea) {
          const text = textarea.value;
          const start = textarea.selectionStart;
          const end = textarea.selectionEnd;
          form.setValue(
            fieldName,
            text.substring(0, start) + operator + text.substring(end),
          );
          setTimeout(() => {
            const pos = start + operator.length;
            textarea.setSelectionRange(pos, pos);
          });
        }
      }}
    >
      {children}
    </Badge>
  );
};

const CalculationDefinitionRemoveButton: React.FC<{
  onClick: () => void;
}> = ({ onClick }) => {
  const { t } = useTranslation(["calculation"]);
  return (
    <Box textAlign="right">
      <Link colorScheme="gray" size="sm" onClick={onClick}>
        {t("calculation:deleteVersion")}
      </Link>
    </Box>
  );
};

const CalculationTypeSelect: React.FC<{
  classification: CalculationClassificationEnum | undefined;
  isDisabled: boolean;
}> = ({ classification, isDisabled }) => {
  const { t } = useTranslation(["calculation", "common"]);

  if (!classification) {
    return null;
  }

  if (classification === CalculationClassificationEnum.fee_basis_category) {
    return <FormHiddenInput name="calculation_type" value="basic" />;
  }

  if (classification === CalculationClassificationEnum.fee_basis) {
    return (
      <FormHiddenInput
        name="calculation_type"
        title={t("common:type")}
        value="advanced"
        shouldUnregister={false}
      />
    );
  }

  return (
    <FormSelect
      name="calculation_type"
      title={t("common:type")}
      isDisabled={isDisabled}
      shouldUnregister={false}
    >
      <SelectOption value="basic">{t("calculation:typeBasic")}</SelectOption>
      <SelectOption value="advanced">
        {t("calculation:typeAdvanced")}
      </SelectOption>
    </FormSelect>
  );
};
